import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Divider,
  Alert
} from '@mui/material';

const StandaloneTreatmentApprovalModal = ({ open, onClose, treatmentDetails, onApprove }) => {
  const [billingStatus, setBillingStatus] = useState('payable'); // 'payable', 'complimentary', 'discounted'
  const [discountType, setDiscountType] = useState('amount'); // 'amount' or 'percentage'
  const [discountValue, setDiscountValue] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  if (!treatmentDetails) return null;

  const { name, base_price, tax_rate } = treatmentDetails;
  const basePrice = parseFloat(base_price) || 0;
  const taxRate = parseFloat(tax_rate) || 0;

  const calculateDiscountAmount = () => {
    if (billingStatus !== 'discounted' || !discountValue) return 0;
    
    const discount = parseFloat(discountValue) || 0;
    
    if (discountType === 'percentage') {
      return basePrice * (discount / 100);
    }
    return discount;
  };

  const calculateFinalAmount = () => {
    if (billingStatus === 'complimentary') return 0;
    
    let billableAmount = basePrice;
    
    if (billingStatus === 'discounted') {
      const discountAmt = calculateDiscountAmount();
      billableAmount = basePrice - discountAmt;
    }
    
    const tax = billableAmount * (taxRate / 100);
    return billableAmount + tax;
  };

  const handleApprove = () => {
    // Validation
    if ((billingStatus === 'complimentary' || billingStatus === 'discounted') && !reason.trim()) {
      setError('Please provide a reason for complimentary or discounted pricing');
      return;
    }

    if (billingStatus === 'discounted') {
      if (!discountValue || parseFloat(discountValue) <= 0) {
        setError('Please enter a valid discount amount');
        return;
      }
      
      if (discountType === 'percentage' && parseFloat(discountValue) >= 100) {
        setError('Discount percentage must be less than 100%');
        return;
      }
      
      if (discountType === 'amount' && parseFloat(discountValue) >= basePrice) {
        setError('Discount amount cannot exceed the base price');
        return;
      }
    }

    const approvalData = {
      billing_status: billingStatus,
      discount_amount: billingStatus === 'discounted' ? calculateDiscountAmount() : 0,
      discount_type: discountType,
      reason: reason,
      approved_by: 'Current User', // TODO: Replace with actual user when auth is implemented
      approved_at: new Date().toISOString()
    };

    onApprove(approvalData);
    
    // Reset form
    setBillingStatus('payable');
    setDiscountType('amount');
    setDiscountValue('');
    setReason('');
    setError('');
  };

  const handleClose = () => {
    // Reset form
    setBillingStatus('payable');
    setDiscountType('amount');
    setDiscountValue('');
    setReason('');
    setError('');
    onClose();
  };

  const formatCurrency = (amount) => {
    return amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: '#fff3e0', display: 'flex', alignItems: 'center', gap: 1 }}>
        <span style={{ fontSize: '24px' }}>💳</span>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Standalone Treatment - Billing Approval
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 4 }}>
        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> This approval is for planning. The guest will only be charged when the treatment is successfully scheduled and booked.
          </Typography>
        </Alert>

        {/* Treatment Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            Treatment:
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {name}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Pricing Summary */}
        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">
              Base Price (Package Rate):
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ₹{formatCurrency(basePrice)}
            </Typography>
          </Box>

          {billingStatus === 'discounted' && discountValue && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                Discount ({discountType === 'percentage' ? `${discountValue}%` : formatCurrency(parseFloat(discountValue))}):
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                - ₹{formatCurrency(calculateDiscountAmount())}
              </Typography>
            </Box>
          )}

          {billingStatus === 'complimentary' && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                Complimentary Discount:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                - ₹{formatCurrency(basePrice)}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">
              Tax ({taxRate}%):
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {billingStatus === 'complimentary' 
                ? '₹0.00' 
                : billingStatus === 'discounted' && discountValue
                ? `₹${formatCurrency((basePrice - calculateDiscountAmount()) * (taxRate / 100))}`
                : `₹${formatCurrency(basePrice * (taxRate / 100))}`
              }
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Total Amount:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ₹{formatCurrency(calculateFinalAmount())}
            </Typography>
          </Box>
        </Box>

        {/* Billing Options */}
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          How should this treatment be billed?
        </Typography>

        <Box sx={{ pl: 2 }}>
          <RadioGroup value={billingStatus} onChange={(e) => setBillingStatus(e.target.value)}>
            <FormControlLabel
              value="payable"
              control={<Radio sx={{ color: '#E4C984', '&.Mui-checked': { color: '#E4C984' }, padding: '0px', marginRight: '8px' }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: '20px' }}>
                    Payable
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Guest pays full amount (₹{formatCurrency(calculateFinalAmount())})
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1.5 }}
            />

            <FormControlLabel
              value="complimentary"
              control={<Radio sx={{ color: '#E4C984', '&.Mui-checked': { color: '#E4C984' }, padding: '0px', marginRight: '8px' }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: '20px' }}>
                    Complimentary
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No charge to guest (₹0.00)
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1.5 }}
            />

            <FormControlLabel
              value="discounted"
              control={<Radio sx={{ color: '#E4C984', '&.Mui-checked': { color: '#E4C984' }, padding: '0px', marginRight: '8px' }} />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: '20px' }}>
                    Discounted
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Apply discount to base price
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1.5 }}
            />
          </RadioGroup>
        </Box>

        {/* Discount Input */}
        {billingStatus === 'discounted' && (
          <Box sx={{ mt: 2, pl: 4 }}>
            {/* Discount Type Selector */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant={discountType === 'amount' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setDiscountType('amount');
                  setDiscountValue('');
                }}
                sx={{
                  flex: 1,
                  bgcolor: discountType === 'amount' ? '#E4C984' : 'transparent',
                  color: discountType === 'amount' ? '#171717' : '#E4C984',
                  borderColor: '#E4C984',
                  '&:hover': {
                    bgcolor: discountType === 'amount' ? '#cbb16f' : 'rgba(228, 201, 132, 0.1)',
                    borderColor: '#E4C984'
                  }
                }}
              >
                Amount (₹)
              </Button>
              <Button
                variant={discountType === 'percentage' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setDiscountType('percentage');
                  setDiscountValue('');
                }}
                sx={{
                  flex: 1,
                  bgcolor: discountType === 'percentage' ? '#E4C984' : 'transparent',
                  color: discountType === 'percentage' ? '#171717' : '#E4C984',
                  borderColor: '#E4C984',
                  '&:hover': {
                    bgcolor: discountType === 'percentage' ? '#cbb16f' : 'rgba(228, 201, 132, 0.1)',
                    borderColor: '#E4C984'
                  }
                }}
              >
                Percentage (%)
              </Button>
            </Box>

            {/* Discount Value Input */}
            <TextField
              fullWidth
              size="small"
              label={discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder={discountType === 'percentage' ? 'e.g. 10' : 'Enter amount'}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>
                  {discountType === 'percentage' ? '%' : '₹'}
                </Typography>,
              }}
              inputProps={{
                min: 0,
                max: discountType === 'percentage' ? 99 : basePrice,
                step: discountType === 'percentage' ? 1 : 0.01
              }}
              sx={{ mb: 1 }}
            />
            {discountValue && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Discount: ₹{formatCurrency(calculateDiscountAmount())}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Guest will pay: ₹{formatCurrency(calculateFinalAmount())}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Reason Input */}
        {(billingStatus === 'complimentary' || billingStatus === 'discounted') && (
          <Box sx={{ mt: 2, pl: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={2}
              placeholder="Enter reason for complimentary/discounted pricing"
            />
          </Box>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleApprove}
          variant="contained"
          sx={{ bgcolor: '#E4C984', color: '#171717', '&:hover': { bgcolor: '#cbb16f' } }}
        >
          Approve & Add to Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StandaloneTreatmentApprovalModal;

